import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';
import { FacebookApiService } from '@vendasta/facebook';
import {
  LeadsApiService,
  LeadSource,
  PartnerFacebookLeadAdsConnectionStatusResponseInterface,
} from '@vendasta/prospect';
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class FacebookAdLeadsService {
  private readonly errorCheckingPartnerConnection$$: Subject<boolean> = new BehaviorSubject<boolean>(false);
  readonly errorCheckingPartnerConnection$ = this.errorCheckingPartnerConnection$$.asObservable();

  readonly connectionStatus$ = this.partnerId$.pipe(
    tap((_) => this.errorCheckingPartnerConnection$$.next(false)),
    tap((_) => this.connectionStatusLoading$$.next(true)),
    switchMap((pid) => this.leadsSDK.partnerFacebookLeadAdsConnectionStatus({ partnerId: pid })),
    catchError(() => {
      this.errorCheckingPartnerConnection$$.next(true);
      this.connectionStatusLoading$$.next(false);
      return EMPTY;
    }),
    map(
      (
        response: PartnerFacebookLeadAdsConnectionStatusResponseInterface,
      ): PartnerFacebookLeadAdsConnectionStatusResponseInterface => ({
        partnerId: response.partnerId || '',
        hasGrantedAccessToken: response.hasGrantedAccessToken || false,
        isConnected: response.isConnected || false,
        connectedPageId: response.connectedPageId || '',
        connectedPageName: response.connectedPageName || '',
        tokenIsValid: response.tokenIsValid || false,
      }),
    ),
    tap((_) => this.connectionStatusLoading$$.next(false)),
    shareReplay(1),
  );
  private readonly connectionStatusLoading$$ = new BehaviorSubject<boolean>(false);
  readonly connectionStatusLoading$ = this.connectionStatusLoading$$.asObservable();

  pageID$ = this.connectionStatus$.pipe(map((s) => s.connectedPageId));
  pageName$ = this.connectionStatus$.pipe(map((s) => s.connectedPageName));
  pageDisplayName$ = this.connectionStatus$.pipe(map((s) => s.connectedPageName || s.connectedPageId));
  tokenIsInvalid$ = this.connectionStatus$.pipe(map((s) => !s.tokenIsValid && s.isConnected));
  partnerHasCompletedConnection$ = this.connectionStatus$.pipe(map((v) => v.isConnected));

  partnerIDAndPageID$ = combineLatest([this.partnerId$, this.pageID$]);

  isDisconnectingPage$$ = new BehaviorSubject(false);
  isDisconnectingPage$ = this.isDisconnectingPage$$.asObservable();
  pageWasDisconnected$ = this.isDisconnectingPage$.pipe(
    // only fire when isDisconnectingPage$ was disconnecting, and then finished
    distinctUntilChanged((prev, current) => prev && !current),
    map((v) => !v),
  );

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly leadsSDK: LeadsApiService,
    private readonly facebookSDK: FacebookApiService,
    private environment: EnvironmentService,
    private router: Router,
  ) {}

  DisconnectFromFacebookPage(): Observable<HttpResponse<null>> {
    this.isDisconnectingPage$$.next(true);
    return this.partnerIDAndPageID$.pipe(
      switchMap(([partnerID, pageID]) => {
        return this.leadsSDK.disconnectLeadsForPage({
          partnerId: partnerID,
          pageId: pageID,
          source: LeadSource.LEAD_SOURCE_FACEBOOK,
        });
      }),
      take(1),
      finalize(() => this.isDisconnectingPage$$.next(false)),
    );
  }

  // NOTE: do not subscribe to this observable unless the user takes action, and make sure to pipe
  // to take(1) before subscribing to it. Otherwise, sessions will be generated in the backend on
  // each page view, instead of being registered only when the user starts to connect.
  urlToSendUserToFacebookToConnect(): Observable<string> {
    return this.partnerId$.pipe(
      switchMap((pid) =>
        this.facebookSDK.registerSessionToConnectLeadAdsForPartner({
          partnerId: pid,
        }),
      ),
      map((response) => {
        const pcHost = window.location.host;
        const pcPath = this.router.createUrlTree(['/facebook-ad-leads', 'connect']).toString();
        const successUrl = `https://${pcHost}${pcPath}`;
        const errorPath = this.router
          .createUrlTree(['/app-integrations'], {
            queryParams: { error: 'true' },
          })
          .toString();
        const errorUrl = `https://${pcHost}${errorPath}`;
        const host =
          this.environment.getEnvironment() === Environment.PROD
            ? 'https://facebook-prod.apigateway.co'
            : 'https://facebook-demo.apigateway.co';
        const params = {
          sessionId: response.sessionId,
          successUrl: successUrl,
          errorUrl: errorUrl,
        };
        const query = new URLSearchParams(params).toString();
        return `${host}/lead-gen/redirect-to-facebook-auth?${query}`;
      }),
    );
  }
}
